/**
 * HVAC Layout Generator
 *
 * Generates visual graph layouts from detected HVAC systems.
 * Positions nodes in a logical arrangement for easy viewing.
 */

import type { Node, Edge, XYPosition } from 'reactflow';
import type { HVACNodeData, HVACEdgeData, FluidMedium } from '../types';
import type {
  HVACDetectionResult,
  DetectedAirLoop,
  DetectedComponent,
  NodeConnection,
} from './types';
import { getComponentDefinition } from '../components';

/**
 * Layout configuration
 */
const LAYOUT_CONFIG = {
  /** Horizontal spacing between components */
  horizontalSpacing: 250,
  /** Vertical spacing between components */
  verticalSpacing: 150,
  /** Starting X position */
  startX: 100,
  /** Starting Y position */
  startY: 100,
  /** Space between air loops */
  loopSpacing: 400,
  /** Zone terminal vertical offset */
  zoneTerminalOffset: 300,
};

/**
 * Generate unique node ID
 */
function generateNodeId(): string {
  return `node_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate unique edge ID
 */
function generateEdgeId(): string {
  return `edge_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get edge color based on fluid medium
 */
function getEdgeColor(medium: FluidMedium): string {
  switch (medium) {
    case 'air':
      return '#64B5F6';
    case 'water':
      return '#42A5F5';
    case 'refrigerant':
      return '#AB47BC';
    case 'steam':
      return '#BDBDBD';
    default:
      return '#90A4AE';
  }
}

/**
 * Create a visual node from a detected component
 */
function createNode(
  component: DetectedComponent,
  position: XYPosition
): Node<HVACNodeData> {
  return {
    id: generateNodeId(),
    type: 'hvacNode',
    position,
    data: {
      componentType: component.componentType,
      name: component.name,
      idfObjectId: component.idfObjectId,
      fields: { ...component.fields },
      hasErrors: false,
      errors: [],
    },
  };
}

/**
 * Create edges from node connections
 */
function createEdges(
  connections: NodeConnection[],
  componentToNode: Map<string, Node<HVACNodeData>>
): Edge<HVACEdgeData>[] {
  const edges: Edge<HVACEdgeData>[] = [];

  for (const conn of connections) {
    const sourceNode = componentToNode.get(conn.source.idfObjectId);
    const targetNode = componentToNode.get(conn.target.idfObjectId);

    if (!sourceNode || !targetNode) continue;

    // Determine medium from source port
    const sourceDef = getComponentDefinition(sourceNode.data.componentType);
    const sourcePort = sourceDef?.ports.find(p => p.id === conn.source.portId);
    const medium: FluidMedium = sourcePort?.medium ?? 'air';

    edges.push({
      id: generateEdgeId(),
      source: sourceNode.id,
      target: targetNode.id,
      sourceHandle: conn.source.portId,
      targetHandle: conn.target.portId,
      type: 'smoothstep',
      animated: true,
      style: {
        stroke: getEdgeColor(medium),
        strokeWidth: 2,
      },
      data: {
        sourcePort: conn.source.portId,
        targetPort: conn.target.portId,
        medium,
        nodeName: conn.nodeName,
      },
    });
  }

  return edges;
}

/**
 * Layout a single air loop
 */
function layoutAirLoop(
  airLoop: DetectedAirLoop,
  startY: number,
  componentToNode: Map<string, Node<HVACNodeData>>
): { nodes: Node<HVACNodeData>[]; maxY: number } {
  const nodes: Node<HVACNodeData>[] = [];
  let currentY = startY;

  // Layout supply components horizontally
  const supplyY = currentY;
  let supplyX = LAYOUT_CONFIG.startX;

  for (const component of airLoop.supplyComponents) {
    const node = createNode(component, { x: supplyX, y: supplyY });
    nodes.push(node);
    componentToNode.set(component.idfObjectId, node);
    supplyX += LAYOUT_CONFIG.horizontalSpacing;
  }

  // Layout demand components (zone terminals) below
  if (airLoop.demandComponents.length > 0) {
    const demandY = supplyY + LAYOUT_CONFIG.zoneTerminalOffset;
    let demandX = LAYOUT_CONFIG.startX + LAYOUT_CONFIG.horizontalSpacing; // Offset slightly

    for (const component of airLoop.demandComponents) {
      const node = createNode(component, { x: demandX, y: demandY });
      nodes.push(node);
      componentToNode.set(component.idfObjectId, node);
      demandX += LAYOUT_CONFIG.horizontalSpacing;
    }

    currentY = demandY + LAYOUT_CONFIG.verticalSpacing;
  } else {
    currentY = supplyY + LAYOUT_CONFIG.verticalSpacing;
  }

  return { nodes, maxY: currentY };
}

/**
 * Layout standalone components
 */
function layoutStandaloneComponents(
  components: DetectedComponent[],
  startY: number,
  componentToNode: Map<string, Node<HVACNodeData>>
): { nodes: Node<HVACNodeData>[]; maxY: number } {
  const nodes: Node<HVACNodeData>[] = [];

  if (components.length === 0) {
    return { nodes, maxY: startY };
  }

  // Group by component category for better organization
  const grouped = new Map<string, DetectedComponent[]>();

  for (const component of components) {
    const def = getComponentDefinition(component.componentType);
    const category = def?.category ?? 'other';
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)?.push(component);
  }

  let currentY = startY;

  for (const [_category, categoryComponents] of grouped) {
    let currentX = LAYOUT_CONFIG.startX;

    for (const component of categoryComponents) {
      const node = createNode(component, { x: currentX, y: currentY });
      nodes.push(node);
      componentToNode.set(component.idfObjectId, node);
      currentX += LAYOUT_CONFIG.horizontalSpacing;

      // Wrap to next row if too wide
      if (currentX > 1500) {
        currentX = LAYOUT_CONFIG.startX;
        currentY += LAYOUT_CONFIG.verticalSpacing;
      }
    }

    currentY += LAYOUT_CONFIG.verticalSpacing;
  }

  return { nodes, maxY: currentY };
}

/**
 * Generate complete visual graph from detection result
 */
export function generateGraphFromDetection(
  result: HVACDetectionResult
): { nodes: Node<HVACNodeData>[]; edges: Edge<HVACEdgeData>[] } {
  const allNodes: Node<HVACNodeData>[] = [];
  const componentToNode = new Map<string, Node<HVACNodeData>>();

  let currentY = LAYOUT_CONFIG.startY;

  // Layout each air loop
  for (const airLoop of result.airLoops) {
    const { nodes, maxY } = layoutAirLoop(airLoop, currentY, componentToNode);
    allNodes.push(...nodes);
    currentY = maxY + LAYOUT_CONFIG.loopSpacing;
  }

  // Layout standalone components
  if (result.standaloneComponents.length > 0) {
    const { nodes } = layoutStandaloneComponents(
      result.standaloneComponents,
      currentY,
      componentToNode
    );
    allNodes.push(...nodes);
  }

  // Create edges from connections
  const edges = createEdges(result.connections, componentToNode);

  return { nodes: allNodes, edges };
}

/**
 * Result of graph generation
 */
export interface GeneratedGraph {
  /** Visual nodes for React Flow */
  nodes: Node<HVACNodeData>[];
  /** Visual edges for React Flow */
  edges: Edge<HVACEdgeData>[];
  /** Map from IDF object ID to visual node ID */
  idfToNodeMap: Map<string, string>;
  /** Statistics */
  stats: {
    nodeCount: number;
    edgeCount: number;
    airLoopCount: number;
  };
}

/**
 * Generate graph with full metadata
 */
export function generateGraph(result: HVACDetectionResult): GeneratedGraph {
  const { nodes, edges } = generateGraphFromDetection(result);

  // Build IDF to node ID map
  const idfToNodeMap = new Map<string, string>();
  for (const node of nodes) {
    if (node.data.idfObjectId) {
      idfToNodeMap.set(node.data.idfObjectId, node.id);
    }
  }

  return {
    nodes,
    edges,
    idfToNodeMap,
    stats: {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      airLoopCount: result.airLoops.length,
    },
  };
}
